/**
 * The Emails overview: how sending is going, before anything else.
 *
 * Every number comes from the email log (`email_events`). Sent is counted
 * when Hawfinch hands an email to Amazon SES; delivered, opened, clicked,
 * bounced and complained arrive afterwards from SES's event feed, so until
 * that feed is connected they read zero and the page says why.
 *
 * Rates are of delivered emails, or of sent ones while no delivery events
 * have arrived. Opens are shown but flagged: Apple Mail opens every email
 * itself to protect privacy, so clicks are the honest engagement number.
 *
 * The per-campaign table lists the campaigns, then every Automation that
 * sent email in the period (its emails are logged as `wf-<id>`, named and
 * linked by the server's `workflow_id`).
 */
import { Alert, Anchor, Badge, Box, Card, Group, Loader, SegmentedControl, SimpleGrid, Stack, Table, Text, Tooltip as MTooltip } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useReducedMotion } from '@mantine/hooks';
import { useEmailCampaigns, useEmailStats, type EmailStatTotals } from '../../api/queries';
import { BAR_CURSOR, CHART_INK, CHART_POSITIVE, GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS, chartAnimation, formatCompactNumber } from '../../charts';
import { TOKENS, formatNumber } from '../../theme';
import { useVocab } from '../../vocab';
import { StatusBadge } from './StatusBadge';
import { ListHealth } from './ListHealth';

const RANGES = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
];

function pct(n: number, of: number): string {
  if (!of) return '-';
  const p = (n / of) * 100;
  return `${p < 10 ? p.toFixed(1) : Math.round(p)}%`;
}

/** What rates are measured against: delivered, or sent before delivery events arrive. */
function base(t: EmailStatTotals): number {
  return t.delivered > 0 ? t.delivered : t.sent;
}

function Stat({ label, value, sub, hint }: { label: string; value: string; sub?: React.ReactNode; hint?: string }) {
  return (
    <Card withBorder radius="md" padding="md">
      <Group gap={4} wrap="nowrap">
        <Text size="sm" c="dimmed">{label}</Text>
        {hint && (
          <MTooltip label={hint} multiline w={240} withArrow>
            <IconInfoCircle size={14} color={TOKENS.textMuted} />
          </MTooltip>
        )}
      </Group>
      <Text fz={26} fw={700} lh={1.2} mt={4}>{value}</Text>
      {sub && <Text size="xs" c="dimmed" mt={2}>{sub}</Text>}
    </Card>
  );
}

function Change({ now, before }: { now: number; before: number }) {
  if (!before) return <>No earlier period to compare</>;
  const d = Math.round(((now - before) / before) * 100);
  const color = d > 0 ? TOKENS.greenText : d < 0 ? TOKENS.redText : undefined;
  return <span style={{ color }}>{d > 0 ? '+' : ''}{d}% on the period before</span>;
}

export function OverviewTab({ range, onRange }: { range: string; onRange: (r: string) => void }) {
  const v = useVocab();
  const stats = useEmailStats(range);
  const campaigns = useEmailCampaigns();
  const reduced = useReducedMotion() ?? false;
  const s = stats.data;
  const t = s?.totals;
  const b = t ? base(t) : 0;
  const h = s?.health;
  const byCampaign = new Map((s?.campaigns ?? []).map((c) => [c.campaign_id, c.totals]));
  const list = campaigns.data?.campaigns ?? [];
  const automations = (s?.campaigns ?? []).filter((c) => !!c.workflow_id);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">All campaigns, all brands.</Text>
        <SegmentedControl size="xs" value={range} onChange={onRange} data={RANGES} />
      </Group>

      {stats.error && <Alert color="red" variant="light">{stats.error.message}</Alert>}
      {h && !h.events_connected && (
        <Alert variant="light" color="gray" icon={<IconInfoCircle size={18} />}>
          Delivered, opened, clicked and bounced fill in once Amazon&apos;s event feed is connected.
          Until then only sends and unsubscribes are counted. Set it up in{' '}
          <Anchor component={Link} to="/settings?tab=organisation">Settings, under Services</Anchor>.
        </Alert>
      )}

      {stats.isPending ? (
        <Group justify="center" py="xl"><Loader size="sm" /></Group>
      ) : t && (
        <>
          <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="sm">
            <Stat label="Sent" value={formatNumber(t.sent)} sub={<Change now={t.sent} before={s.previous.sent} />} />
            <Stat label="Delivered" value={pct(t.delivered, t.sent)} sub={`${formatNumber(t.delivered)} emails`} />
            <Stat
              label="Opened"
              value={pct(t.opened, b)}
              sub={`${formatNumber(t.opened)} emails`}
              hint="Higher than the truth: Apple Mail opens every email itself to protect privacy. Clicks are the better measure."
            />
            <Stat label="Clicked" value={pct(t.clicked, b)} sub={`${formatNumber(t.clicked)} emails`} />
            <Stat label="Unsubscribed" value={pct(t.unsubscribed, b)} sub={`${formatNumber(t.unsubscribed)} people`} />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="sm">
            <Card withBorder radius="md" padding="md" style={{ gridColumn: 'span 2' }}>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={600}>Sent and opened per day</Text>
                <Group gap="md">
                  <Group gap={6}><Box w={10} h={10} style={{ background: CHART_INK, borderRadius: 2 }} /><Text size="xs" c="dimmed">Sent</Text></Group>
                  <Group gap={6}><Box w={10} h={10} style={{ background: CHART_POSITIVE, borderRadius: 2 }} /><Text size="xs" c="dimmed">Opened</Text></Group>
                </Group>
              </Group>
              {s.daily.length === 0 ? (
                <Text size="sm" c="dimmed" py="xl" ta="center">Nothing sent in this period yet.</Text>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={s.daily.map((d) => ({ ...d, label: d.day.slice(5) }))} margin={{ top: 6, right: 0, bottom: 0, left: -4 }} barGap={2}>
                    <CartesianGrid {...GRID_PROPS} />
                    <XAxis dataKey="label" {...X_AXIS_PROPS} minTickGap={14} />
                    <YAxis {...Y_AXIS_PROPS} tickFormatter={formatCompactNumber} allowDecimals={false} />
                    <Tooltip cursor={BAR_CURSOR} isAnimationActive={false} />
                    <Bar dataKey="sent" name="Sent" fill={CHART_INK} radius={[3, 3, 0, 0]} maxBarSize={18} {...chartAnimation(reduced)} />
                    <Bar dataKey="opened" name="Opened" fill={CHART_POSITIVE} radius={[3, 3, 0, 0]} maxBarSize={18} {...chartAnimation(reduced)} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Card withBorder radius="md" padding="md">
              <Text size="sm" fw={600} mb="xs">Sending health</Text>
              <Stack gap={8}>
                <HealthRow label="Bounce rate" value={h?.bounce_rate ?? 0} limit={0.05} warnAt={0.02}
                  hint="Amazon reviews accounts above 5%. Permanent bounces go on the never-email list automatically." />
                <HealthRow label="Spam complaints" value={h?.complaint_rate ?? 0} limit={0.001} warnAt={0.0005}
                  hint="Amazon reviews accounts above 0.1%. Anyone who complains goes on the never-email list." />
                <Group justify="space-between">
                  <Text size="sm">Sent in last 24 hours</Text>
                  <Text size="sm" fw={600}>
                    {h?.max_24h ? `${formatNumber(h.sent_24h)} of ${formatNumber(h.max_24h)}` : '-'}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Amazon account</Text>
                  {h?.production == null ? (
                    <MTooltip label={h?.account_error || 'Not checked'} multiline w={260}><Badge color="gray" variant="light">Unknown</Badge></MTooltip>
                  ) : h.production ? (
                    <Badge color="teal" variant="light">Production</Badge>
                  ) : (
                    <MTooltip label="Only verified addresses, up to 200 a day. Ask Amazon for production access to send to leads." multiline w={260}>
                      <Badge color="chartAmber" variant="light">Sandbox</Badge>
                    </MTooltip>
                  )}
                </Group>
              </Stack>
            </Card>
          </SimpleGrid>

          <Card withBorder radius="md" padding="md">
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={600}>Bounced per day</Text>
              <Text size="xs" c="dimmed">
                {`${formatNumber(t.bounced)} in this period, ${pct(t.bounced, t.sent)} of sent`}
              </Text>
            </Group>
            {!h?.events_connected ? (
              <Text size="sm" c="dimmed" py="md" ta="center">Bounces show here once Amazon&apos;s event feed is connected.</Text>
            ) : s.daily.length === 0 ? (
              <Text size="sm" c="dimmed" py="md" ta="center">Nothing sent in this period yet.</Text>
            ) : (
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={s.daily.map((d) => ({ ...d, label: d.day.slice(5) }))} margin={{ top: 6, right: 0, bottom: 0, left: -4 }}>
                  <CartesianGrid {...GRID_PROPS} />
                  <XAxis dataKey="label" {...X_AXIS_PROPS} minTickGap={14} />
                  <YAxis {...Y_AXIS_PROPS} tickFormatter={formatCompactNumber} allowDecimals={false} />
                  <Tooltip
                    cursor={BAR_CURSOR}
                    isAnimationActive={false}
                    formatter={(value, _name, item) => [
                      `${formatNumber(Number(value))} (${pct(Number(value), Number(item.payload?.sent ?? 0))} of sent)`,
                      'Bounced',
                    ]}
                  />
                  <Bar dataKey="bounced" name="Bounced" fill={TOKENS.red} radius={[3, 3, 0, 0]} maxBarSize={18} {...chartAnimation(reduced)} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          <ListHealth />

          <Card withBorder radius="md" padding={0}>
            <Text size="sm" fw={600} p="md" pb="xs">{`Campaigns and ${v.many('workflow')}`}</Text>
            <Table.ScrollContainer minWidth={720}>
              <Table verticalSpacing={8} horizontalSpacing="md">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th ta="right">Sent</Table.Th>
                    <Table.Th ta="right">Delivered</Table.Th>
                    <Table.Th ta="right">Opened</Table.Th>
                    <Table.Th ta="right">Clicked</Table.Th>
                    <Table.Th ta="right">Unsubscribed</Table.Th>
                    <Table.Th ta="right">Bounced</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {list.length === 0 && automations.length === 0 && (
                    <Table.Tr><Table.Td colSpan={8}><Text size="sm" c="dimmed" ta="center" py="md">No campaigns yet. Make one under Campaigns.</Text></Table.Td></Table.Tr>
                  )}
                  {list.map((c) => {
                    const ct = byCampaign.get(c.id);
                    const cb = ct ? base(ct) : 0;
                    return (
                      <Table.Tr key={c.id}>
                        <Table.Td>
                          <Text size="sm" fw={600}>{c.name}</Text>
                          <Text size="xs" c="dimmed">{c.kind === 'oneoff' ? 'One-off' : 'Sent to each new lead'}{c.brand_name ? ` · ${c.brand_name}` : ''}</Text>
                        </Table.Td>
                        <Table.Td><StatusBadge status={c.status} /></Table.Td>
                        <Table.Td ta="right">{formatNumber(ct?.sent ?? 0)}</Table.Td>
                        <Table.Td ta="right">{ct ? pct(ct.delivered, ct.sent) : '-'}</Table.Td>
                        <Table.Td ta="right">{ct ? pct(ct.opened, cb) : '-'}</Table.Td>
                        <Table.Td ta="right">{ct ? pct(ct.clicked, cb) : '-'}</Table.Td>
                        <Table.Td ta="right">{ct ? formatNumber(ct.unsubscribed) : '-'}</Table.Td>
                        <Table.Td ta="right">{ct ? formatNumber(ct.bounced) : '-'}</Table.Td>
                      </Table.Tr>
                    );
                  })}
                  {automations.map((a) => {
                    const ab = base(a.totals);
                    return (
                      <Table.Tr key={a.campaign_id} data-testid="overview-automation-row">
                        <Table.Td>
                          <Anchor component={Link} to={`/automations/${encodeURIComponent(a.workflow_id!)}?tab=analytics`} size="sm" fw={600}>
                            {a.name || `A deleted ${v.one('workflow')}`}
                          </Anchor>
                        </Table.Td>
                        <Table.Td><Badge color="gray" variant="light" tt="none">{v.One('workflow')}</Badge></Table.Td>
                        <Table.Td ta="right">{formatNumber(a.totals.sent)}</Table.Td>
                        <Table.Td ta="right">{pct(a.totals.delivered, a.totals.sent)}</Table.Td>
                        <Table.Td ta="right">{pct(a.totals.opened, ab)}</Table.Td>
                        <Table.Td ta="right">{pct(a.totals.clicked, ab)}</Table.Td>
                        <Table.Td ta="right">{formatNumber(a.totals.unsubscribed)}</Table.Td>
                        <Table.Td ta="right">{formatNumber(a.totals.bounced)}</Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Card>
        </>
      )}
    </Stack>
  );
}

function HealthRow({ label, value, limit, warnAt, hint }: { label: string; value: number; limit: number; warnAt: number; hint: string }) {
  const color = value >= limit ? TOKENS.redText : value >= warnAt ? TOKENS.amberText : TOKENS.greenText;
  return (
    <Group justify="space-between">
      <Group gap={4}>
        <Text size="sm">{label}</Text>
        <MTooltip label={hint} multiline w={260} withArrow><IconInfoCircle size={14} color={TOKENS.textMuted} /></MTooltip>
      </Group>
      <Text size="sm" fw={600} style={{ color }}>
        {(value * 100).toFixed(2)}% <Text span size="xs" c="dimmed" fw={400}>of {(limit * 100).toFixed(1)}%</Text>
      </Text>
    </Group>
  );
}
